import { BaseButton, BaseFileUploaderContainer, ProviderCreateDocumentType, SelectField, ToastContext } from '@enter/core';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import * as Yup from 'yup';
import { DialogContext } from '../../services';
import { IInjectedRootStore } from '../../stores';
import * as S from './document-upload-dialog.styles';

interface IDocumentUploadDialogProps extends IInjectedRootStore {
    onClose: () => void;
    onUploaded?: () => Promise<void> | void;
}

type DocumentUploadForm = {
    documentType: ProviderCreateDocumentType | null;
    file: File | null;
};

const DOCUMENT_TYPES: {
    value: ProviderCreateDocumentType;
    label: string;
    info: string;
}[] = [
    {
        value: ProviderCreateDocumentType.AICLAIM,
        label: 'Claim',
        info: ' - A single encounter including patient demographics, insurance, rendered procedures, and prior authorizations used in the manual creation of a claim.',
    },
    {
        value: ProviderCreateDocumentType.AIFAX,
        label: 'Fax',
        info: ' - A scanned fax received by the practice to be sorted and posted to claims.',
    },
    {
        value: ProviderCreateDocumentType.AIMAIL,
        label: 'Mail',
        info: ' - A scanned letter received by the practice mailbox to be sorted and posted to claims.',
    },
];

const validationSchema = Yup.object().shape({
    documentType: Yup.mixed<ProviderCreateDocumentType>().required('Document type is required'),
    file: Yup.mixed<File>()
        .required('A document file is required')
        .test('fileProvided', 'A document file is required', value => value instanceof File),
});

const initialValues: DocumentUploadForm = {
    documentType: null,
    file: null,
};

export const DocumentUploadDialog: React.FunctionComponent<IDocumentUploadDialogProps> = inject('rootStore')(
    observer((props: IDocumentUploadDialogProps): React.ReactElement => {
        const { rootStore, onClose, onUploaded } = props;
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        if (!rootStore) {
            return <></>;
        }

        const handleCancel = (): void => {
            onClose?.();
        };

        const handleSubmit = async (
            values: DocumentUploadForm,
            { setSubmitting }: FormikHelpers<DocumentUploadForm>,
        ): Promise<void> => {
            try {
                if (!values.documentType || !values.file) {
                    setSubmitting(false);
                    return;
                }

                await rootStore.documentStore.uploadDocument({
                    documentType: values.documentType,
                    file: values.file,
                    description: "Provider uploaded documentation.",
                });

                toastContext.notify?.('Document uploaded');
                await onUploaded?.();
                onClose?.();
            } catch (err) {
                dialogContext.showError(err as Error);
            } finally {
                setSubmitting(false);
            }
        };

        return (
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {(formik: FormikProps<DocumentUploadForm>): React.ReactElement => {
                    const selectedType = DOCUMENT_TYPES.find(option => option.value === formik.values.documentType);
                    const selectedFile = formik.values.file;
                    const selectedFileSize =
                        selectedFile && selectedFile.size ? `${(selectedFile.size / 1024).toFixed(1)} KB` : undefined;

                    return (
                        <S.DialogBody>
                            <S.Section>
                                <S.Label>Document Type</S.Label>
                                <S.FormField
                                    name="documentType"
                                    component={SelectField}
                                    placeholder="Select a document type"
                                    options={DOCUMENT_TYPES}
                                />
                                {selectedType && <S.Info>{selectedType.info}</S.Info>}
                            </S.Section>

                            <S.Section>
                                <S.Label>Document File</S.Label>
                                <S.Description>Select a file to upload for the selected document type.</S.Description>
                                <BaseFileUploaderContainer
                                    accept="application/pdf,image/*"
                                    onChange={file => {
                                        formik.setFieldValue('file', file);
                                        formik.setFieldTouched('file', true, false);
                                    }}>
                                    {({ onClick }) => (
                                        <S.FileActions>
                                            <BaseButton type="button" variant="primary" onClick={onClick}>
                                                {formik.values.file ? 'Replace File' : 'Choose File'}
                                            </BaseButton>
                                            {selectedFile && (
                                                <BaseButton
                                                    type="button"
                                                    variant="warning"
                                                    onClick={() => formik.setFieldValue('file', null)}>
                                                    ✕ Remove
                                                </BaseButton>
                                            )}
                                        </S.FileActions>
                                    )}
                                </BaseFileUploaderContainer>
                                {selectedFile && (
                                    <S.FileRow>
                                        <span>{selectedFile.name}</span>
                                        {selectedFileSize && <span>{selectedFileSize}</span>}
                                    </S.FileRow>
                                )}
                                {formik.touched.file &&
                                    typeof formik.errors.file === 'string' &&
                                    formik.errors.file && <S.ErrorMessage>{formik.errors.file}</S.ErrorMessage>}
                            </S.Section>

                            <S.Actions>
                                <BaseButton variant="outlined" onClick={handleCancel}>
                                    Cancel
                                </BaseButton>
                                <BaseButton
                                    variant="primary"
                                    disabled={
                                        !formik.values.documentType ||
                                        !formik.values.file ||
                                        formik.isSubmitting ||
                                        !formik.isValid
                                    }
                                    onClick={formik.submitForm}>
                                    Upload
                                </BaseButton>
                            </S.Actions>
                        </S.DialogBody>
                    );
                }}
            </Formik>
        );
    }),
);

